import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  RoleWriteInput,
  RolesListQuery,
  RolesRepository,
} from "../domain";
import { useGrantablePermissions } from "../../../shared/application/useGrantablePermissions";

export { useGrantablePermissions };

function params(
  query: RolesListQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useRolesQuery(
  repository: RolesRepository,
  query?: RolesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.roles.list(params(query)),
    queryFn: () => repository.list(query),
  });
}
export function useRoleQuery(repository: RolesRepository, id: string) {
  return useQuery({
    queryKey: queryKeys.roles.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}
function useRolesMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.roles.all }),
  });
}
export function useCreateRoleMutation(repository: RolesRepository) {
  return useRolesMutation((input: RoleWriteInput) => repository.create(input));
}
export function useUpdateRoleMutation(repository: RolesRepository) {
  return useRolesMutation(
    ({ id, input }: { id: string; input: RoleWriteInput }) =>
      repository.update(id, input),
  );
}
export function useDeleteRoleMutation(repository: RolesRepository) {
  return useRolesMutation((id: string) => repository.delete(id));
}
